/**
 * Which flavour of model is this? (card t_14696d19; USDZ added by t_92f7411f)
 *
 * A model reaches a dashboard as one of THREE shapes, and the loader has to
 * know which one it is holding before it opens the bytes:
 *
 *   · a **GLB/glTF** (mesh + colours — what three.js eats natively, and what the
 *     scan pipeline emits as `model.glb`);
 *   · an **IFC** (semantic walls/doors/windows — what the dashboard's BIM
 *     widget ingests through web-ifc, and what the demo house is served as);
 *   · a **USDZ** — the file a raw phone capture carries before any conversion
 *     (`room-1.usdz`), which three's own `USDLoader` reads (card t_92f7411f).
 *
 * web-ifc can read neither a GLB nor a USDZ, three's GLTFLoader cannot read a
 * STEP file, and the USD loader cannot read either of the other two.
 *
 * The extension is not enough. The hub serves every model through ONE route —
 * `/api/dashboards/:id/bim-models/:modelId/file` — which carries no extension
 * at all, and the row's own `name` is whatever the operator typed into the
 * upload form. So the content is the authority:
 *
 *   · a GLB opens with the four ASCII bytes `glTF` (the little-endian magic
 *     0x46546C67) — the spec's own identifier, checkable in four bytes;
 *   · an IFC is a STEP file and opens with `ISO-10303-21;`;
 *   · a `.gltf` is JSON whose root object carries an `asset` block;
 *   · a USDZ is a ZIP (`PK\x03\x04`) whose FIRST entry is the package's root
 *     layer — a `.usda`/`.usdc` file, which the USDZ spec requires and which is
 *     also what keeps a plain `.zip` (or an IFCZIP) from being mistaken for
 *     one. Measured on the real RoomPlan capture (`room-1.usdz`, 258 KB): the
 *     first entry is `room-1.usda`.
 *
 * `resolveBimModelFormat` prefers the sniffed format and falls back to the NAME
 * — the upload path validates a file it has not read yet — and answers `null`
 * when neither says anything, which the loader reads as "the format this app
 * loaded before the split" (its IFC path).
 *
 * Pure on purpose: the rule is a plain Node unit test (`bimModelFormat.test.ts`),
 * so this module must stay importable without three, Vue or a browser.
 */

export type BimModelFormat = 'ifc' | 'glb' | 'gltf' | 'usdz';

/**
 * The import surfaces' ONE accept list (`BimModelUploadModal`, the dashboard
 * editor's model field). Kept here so the picker's `accept`, the validation and
 * the loader's own vocabulary cannot drift apart.
 * USDZ is deliberately NOT on this list (card t_92f7411f): the manual picker
 * offers the three formats the hub's own bim-model route advertises, while a
 * scan's USDZ — a package that carries no GLB — is attached by the scan path,
 * which reads the file out of the hub's scan package instead of a dialog. A
 * USDZ picked from disk travels as `model/vnd.usdz+zip`, which that route
 * refuses, so offering it here would be a button that fails.
 */
export const BIM_MODEL_ACCEPT = '.ifc,.glb,.gltf';

/** The same three extensions, spelled the way the copy lists them. */
export const BIM_MODEL_EXTENSIONS = ['.ifc', '.glb', '.gltf'] as const;

const BY_EXTENSION: Record<string, BimModelFormat> = {
  '.ifc': 'ifc',
  '.glb': 'glb',
  '.gltf': 'gltf',
  '.usdz': 'usdz',
};

/** `true` when the payload has to go through three's GLTFLoader. */
export function isGltfFormat(format: BimModelFormat | null | undefined): boolean {
  return format === 'glb' || format === 'gltf';
}

/** `true` when the payload has to go through three's USD loader (`USDLoader`). */
export function isUsdzFormat(format: BimModelFormat | null | undefined): boolean {
  return format === 'usdz';
}

/**
 * Is this one of the formats the manual upload picker offers
 * (`BIM_MODEL_ACCEPT`)? The loader reads one format more than the picker
 * offers — a USDZ arrives with a scan — so the two lists are asked about
 * separately rather than by reusing `null` as "not ours".
 */
export function isBimModelUploadFormat(format: BimModelFormat | null | undefined): boolean {
  return format === 'ifc' || format === 'glb' || format === 'gltf';
}

/**
 * The format a file NAME claims: its extension, lowercased. A query string or
 * fragment is stripped first, because the loader is handed URLs
 * (`…/file?token=…`) as often as it is handed file names.
 */
export function bimModelFormatFromName(name: unknown): BimModelFormat | null {
  if (typeof name !== 'string') return null;
  const path = name.trim().split(/[?#]/)[0];
  const dot = path.lastIndexOf('.');
  if (dot < 0) return null;
  return BY_EXTENSION[path.slice(dot).toLowerCase()] ?? null;
}

/** The GLB magic, byte for byte: `g`, `l`, `T`, `F`. */
const GLB_MAGIC = [0x67, 0x6c, 0x54, 0x46];

/** A ZIP local file header's signature — what a USDZ opens with. */
const ZIP_MAGIC = [0x50, 0x4b, 0x03, 0x04];

/** How much of a payload is decoded as text to recognise the two text formats. */
const SNIFF_BYTES = 1024;

function startsWith(view: Uint8Array, magic: number[]): boolean {
  return view.byteLength >= magic.length && magic.every((byte, index) => view[index] === byte);
}

/**
 * The first entry's name in a ZIP's local file header: the two bytes at offset
 * 26 are its length (little endian) and the name follows at offset 30. Read by
 * hand rather than by unzipping a whole capture on every load — the answer is
 * decided by the first ~40 bytes.
 */
function firstZipEntryName(view: Uint8Array): string | null {
  if (view.byteLength < 30) return null;
  const length = view[26] | (view[27] << 8);
  if (length <= 0 || view.byteLength < 30 + length) return null;
  return new TextDecoder('latin1').decode(view.subarray(30, 30 + length));
}

function decodeHead(view: Uint8Array): string {
  // A UTF-8 BOM is three bytes of noise the text formats do not mean: decode
  // past it, so a re-saved file that carries one still opens with its keyword.
  const start = view.byteLength >= 3 && view[0] === 0xef && view[1] === 0xbb && view[2] === 0xbf ? 3 : 0;
  // latin1 never throws on arbitrary bytes — this runs before anything has
  // decided that the payload is even text.
  return new TextDecoder('latin1').decode(
    view.subarray(start, Math.min(view.byteLength, start + SNIFF_BYTES)),
  );
}

/**
 * The format the BYTES are, or `null` when they identify as none of ours.
 *
 * Order matters: the unambiguous four-byte magics first (a GLB, then the ZIP a
 * USDZ is), then the STEP header, then the glTF JSON shape. A GLB's own JSON
 * chunk is only reached *after* its magic, so the JSON test can never claim a
 * binary file — and the ZIP test looks only at the OUTER payload's first entry
 * name, never inside the archive.
 */
export function bimModelFormatFromBytes(
  bytes: ArrayBuffer | Uint8Array | null | undefined,
): BimModelFormat | null {
  if (!bytes) return null;
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if (startsWith(view, GLB_MAGIC)) return 'glb';
  if (startsWith(view, ZIP_MAGIC) && /\.usd[ac]$/i.test(firstZipEntryName(view) ?? '')) return 'usdz';
  const head = decodeHead(view);
  // A STEP file opens with the header keyword; leading whitespace (and a BOM)
  // are tolerated because a re-saved file may carry them.
  if (/^\s*ISO-10303-21/.test(head)) return 'ifc';
  // glTF JSON: an object that names its own asset block. A bare `{` is not
  // enough — plenty of JSON travels the same wire (an error body, a manifest).
  const text = head.trimStart();
  if (text.startsWith('{') && /"asset"\s*:/.test(text)) return 'gltf';
  return null;
}

/**
 * The format a payload is, in the order the two pieces of evidence are trusted:
 * the bytes first, the name second (`null` when neither answers).
 */
export function resolveBimModelFormat(input: {
  name?: unknown;
  bytes?: ArrayBuffer | Uint8Array | null;
}): BimModelFormat | null {
  return bimModelFormatFromBytes(input.bytes) ?? bimModelFormatFromName(input.name) ?? null;
}

/**
 * A file name without its model extension — the default name the upload form
 * offers (`room-1.glb` → `room-1`). Only the supported extensions are stripped;
 * a `.ifczip` is a name, not one of ours.
 */
export function stripBimModelExtension(name: unknown): string {
  if (typeof name !== 'string') return '';
  const trimmed = name.trim();
  const format = bimModelFormatFromName(trimmed);
  if (format === null) return trimmed;
  return trimmed.slice(0, trimmed.length - (format.length + 1)); // '.' + extension
}
